import {get, post, put} from './http';

export type ConfigItemStatus = 'active' | 'inactive' | 'archived' | 'deprecated';

export type ConfigItem = {
  id: string;
  type: string;
  name: string;
  parent_id: string | null;
  metadata: Record<string, unknown>;
  sort_order: number;
  status: ConfigItemStatus;
  description: string | null;
  start_date: string | null;
  end_date: string | null;
  created_at: string;
  updated_at: string | null;
  created_by: string | null;
  updated_by: string | null;
};

/**
 A topic to create or replace. Update is a full replace, not a partial patch — the form submits
 every field, and a nullable date in a patch cannot say "clear it" as distinct from "untouched".
 */
export type TopicWriteRequest = {
  name: string;
  description: string | null;
  status: 'active' | 'inactive' | 'archived';
  start_date: string | null;
  end_date: string | null;
};

export const configApi = {
  locations: async () => get<ConfigItem[]>('/config/locations'),
  topics: async () => get<ConfigItem[]>('/config/topics'),

  // Every topic that has ever existed, for filtering submissions. A submission classified last
  // year carries the name it held then, so a filter built from today's available set quietly
  // loses the submissions behind every archived or expired topic.
  allTopics: async () => get<ConfigItem[]>('/config/topics', {include_retired: true}),

  // Without a search term this is the default-visible set only. The full curated ISO 639-3 list is
  // ~7,000 entries — a megabyte of JSON — so the rare ones are reached by searching, server-side.
  languages: async (q?: string) => get<ConfigItem[]>('/config/languages', q ? {q} : undefined),

  // Admin listing, every row whatever its status — an admin managing a lifecycle has to see the
  // entries that are out of it. Languages, locations and core risks are owned by the reference-data
  // packs and applied on deploy, so there is no write call for them: to change those, change the
  // pack. Topics have no upstream producer and are managed here.
  list: async (type: string) => get<ConfigItem[]>(`/admin/config/${type}`),

  createTopic: async (body: TopicWriteRequest) =>
    post<TopicWriteRequest, ConfigItem>('/admin/config/topic', body),
  updateTopic: async (id: string, body: TopicWriteRequest) =>
    put<TopicWriteRequest, ConfigItem>(`/admin/config/topic/${id}`, body)
};
